import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { History } from 'history';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { RootState } from 'store/reducers';

export const useStore = <T>(selector: (state: RootState) => T) => {
  const state = useSelector((rootState: RootState) => rootState);

  return selector(state);
};

export type Router<Params = {}, State = {}> = {
  params: Params;
  push(path: string, params: Params): void;
  replace(path: string, state: State): void;
};

export type RouterResult<Params = object, State = object> = {
  history: History<unknown>;
  pathname: string;
  state: State;
  params: Params;
  canGoBack: boolean;
  push<P = Params, S = State>(path: string, params?: P | undefined, state?: S | undefined): void;
  replace<P = Params, S = State>(path: string, params?: P | undefined, state?: S | undefined): void;
  goBack(): void;
};

export const useRouter = <Params extends object = {}, State extends object = {}>(): RouterResult<Params, State> => {
  const location = useLocation<State>();
  const history = useHistory();
  const match = useRouteMatch<Params>();

  const pathname = useMemo(() => location.pathname, [location.pathname]);
  const state = useMemo(() => location.state || {}, [location.state]);
  // SOURCE: https://stackoverflow.com/a/66137051/9419218
  const canGoBack = history.action !== 'POP';

  const params = useMemo(
    () =>
      ({
        ...match.params,
        ...qs.parse(location.search, { ignoreQueryPrefix: true })
      } as Params),
    [match.params, location.search]
  );

  const push = useCallback(
    <P = Params, S = State>(_pathname: string, _params?: P, _state?: S) =>
      history.push({
        pathname: _pathname,
        search: qs.stringify(_params),
        state: _state
      }),
    [history]
  );

  const replace = useCallback(
    <P = Params, S = State>(_pathname: string, _params?: P, _state?: S) =>
      history.replace({
        pathname: _pathname,
        search: qs.stringify(_params),
        state: _state
      }),
    [history]
  );

  const goBack = () => history.goBack();

  return { history, pathname, state, params, canGoBack, push, replace, goBack };
};

export const useRender = () => {
  const [, setCount] = useState(0);

  const forceUpdate = useCallback(() => setCount(prev => prev + 1), []);

  return {
    forceUpdate
  };
};

export const useLatest = <T>(value: T): React.MutableRefObject<T> => {
  const latestValue = useRef<T>(value);

  useEffect(() => {
    latestValue.current = value;
  }, [JSON.stringify({ value })]);

  return latestValue;
};
